export function CurriculumJourney({ lessons, learningProgress, unlockedModules, onNavigate, theme }) {
  const TYPE_CONFIG = {
    border: { label: 'Border Piece', color: '#d97706', icon: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5z' },
    warning: { label: 'Warning', color: '#ef4444', icon: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z' },
    landmark: { label: 'Landmark', color: '#3b82f6', icon: 'M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z' },
    interior: { label: 'Interior Piece', color: '#8b5cf6', icon: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25' },
  };

  const borderLessons = lessons.filter(l => l.lesson_type === 'border');
  const warnLandLessons = lessons.filter(l => l.lesson_type === 'warning' || l.lesson_type === 'landmark');
  const interiorLessons = lessons.filter(l => l.lesson_type === 'interior');

  const phases = [
    { key: 'borders', title: 'The Border Pieces', subtitle: 'Complete the frame — six straight-edge pieces connecting corner to corner', lessons: borderLessons },
    { key: 'warnland', title: 'Warnings & Landmarks', subtitle: 'Navigate the dangers and discover the key landmarks along the path', lessons: warnLandLessons },
  ];
  if (interiorLessons.length > 0) {
    phases.push({ key: 'interior', title: 'The Interior Pieces', subtitle: 'Deep dives into the appendices — fill in the full picture', lessons: interiorLessons });
  }

  return (
    <div className="px-4 py-12" style={{ borderTop: `1px solid ${theme?.borderPrimary || 'rgba(120,80,20,0.12)'}` }} data-testid="zone-3-curriculum">
      {/* Crown Jewel keyframe styles */}
      <style>{`
        @keyframes keystoneGlow {
          0%, 100% { box-shadow: 0 0 15px rgba(212,165,74,0.15), 0 0 30px rgba(212,165,74,0.05), inset 0 1px 0 rgba(255,215,0,0.08); }
          50% { box-shadow: 0 0 25px rgba(212,165,74,0.35), 0 0 50px rgba(212,165,74,0.12), inset 0 1px 0 rgba(255,215,0,0.15); }
        }
        @keyframes keystoneShimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes crownPulse {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 3px rgba(255,215,0,0.3)); }
          50% { transform: scale(1.08); filter: drop-shadow(0 0 8px rgba(255,215,0,0.6)); }
        }
      `}</style>

      <div className="text-center mb-10">
        <p className="text-xs tracking-[0.5em] uppercase mb-4" style={{ color: theme?.labelColor || '#dc2626' }}>THE JOURNEY — YOUR LEARNING PATH</p>
        <p className="text-sm max-w-xl mx-auto leading-relaxed italic" style={{ color: theme?.textSecondary || 'rgba(220,190,130,0.5)' }}>
          After the Legend Keys and Corner Pieces, the path continues through borders, warnings, landmarks, and deep-dive appendices.
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-10">
        {phases.map(phase => (
          <div key={phase.key} data-testid={`phase-${phase.key}`}>
            <div className="mb-4">
              <h3 className="text-sm tracking-[0.2em] uppercase font-bold mb-1" style={{ color: theme?.accentPrimary || '#d4a54a' }}>{phase.title}</h3>
              <p className="text-xs italic" style={{ color: theme?.textMuted || 'rgba(180,140,70,0.45)' }}>{phase.subtitle}</p>
            </div>
            <div className="space-y-2">
              {phase.lessons.map((lesson) => {
                const cfg = TYPE_CONFIG[lesson.lesson_type] || TYPE_CONFIG.interior;
                const moduleKey = lesson.module_key;
                const isUnlocked = unlockedModules?.includes(moduleKey);
                const progress = learningProgress?.[moduleKey];
                const isCompleted = progress?.status === 'completed';
                const isInProgress = progress?.status === 'in_progress';
                const isLocked = !isUnlocked && !isCompleted;
                const isKeystone = moduleKey === 'BORDER_6';

                // Crown Jewel: BORDER_6 — The Keystone
                if (isKeystone) {
                  return (
                    <div key={moduleKey} className="my-6" data-testid={`curriculum-${moduleKey}`}>
                      {/* Decorative separator above */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,165,74,0.4), transparent)' }} />
                        <span className="text-[9px] tracking-[0.4em] uppercase font-bold" style={{ color: '#d4a54a' }}>The Keystone</span>
                        <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,165,74,0.4), transparent)' }} />
                      </div>

                      <button
                        onClick={() => !isLocked && onNavigate(`/lesson/${moduleKey}`)}
                        disabled={isLocked}
                        className="w-full text-left relative overflow-hidden rounded-2xl border-2 transition-all duration-500 group"
                        style={{
                          background: isCompleted
                            ? 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(16,185,129,0.02))'
                            : isLocked
                            ? 'linear-gradient(135deg, rgba(20,15,5,0.6), rgba(10,8,4,0.8))'
                            : 'linear-gradient(135deg, rgba(30,22,8,0.95), rgba(15,12,5,0.98))',
                          borderColor: isCompleted ? 'rgba(16,185,129,0.4)' : isLocked ? 'rgba(120,80,20,0.1)' : 'rgba(212,165,74,0.5)',
                          animation: !isLocked && !isCompleted ? 'keystoneGlow 3s ease-in-out infinite' : 'none',
                          opacity: isLocked ? 0.55 : 1,
                          cursor: isLocked ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {/* Shimmer overlay */}
                        {!isLocked && (
                          <div className="absolute inset-0 pointer-events-none" style={{
                            background: 'linear-gradient(90deg, transparent 30%, rgba(255,215,0,0.04) 50%, transparent 70%)',
                            backgroundSize: '200% 100%',
                            animation: 'keystoneShimmer 4s linear infinite',
                          }} />
                        )}

                        {/* Corner ornaments */}
                        <div className="absolute top-0 left-0 w-6 h-6" style={{ borderTop: '2px solid rgba(212,165,74,0.4)', borderLeft: '2px solid rgba(212,165,74,0.4)', borderTopLeftRadius: '14px' }} />
                        <div className="absolute top-0 right-0 w-6 h-6" style={{ borderTop: '2px solid rgba(212,165,74,0.4)', borderRight: '2px solid rgba(212,165,74,0.4)', borderTopRightRadius: '14px' }} />
                        <div className="absolute bottom-0 left-0 w-6 h-6" style={{ borderBottom: '2px solid rgba(212,165,74,0.4)', borderLeft: '2px solid rgba(212,165,74,0.4)', borderBottomLeftRadius: '14px' }} />
                        <div className="absolute bottom-0 right-0 w-6 h-6" style={{ borderBottom: '2px solid rgba(212,165,74,0.4)', borderRight: '2px solid rgba(212,165,74,0.4)', borderBottomRightRadius: '14px' }} />

                        <div className="relative px-6 py-6">
                          <div className="flex items-start gap-5">
                            {/* Crown icon */}
                            <div className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
                              style={{
                                background: isCompleted ? 'rgba(16,185,129,0.12)' : 'linear-gradient(135deg, rgba(212,165,74,0.15), rgba(180,130,50,0.08))',
                                border: `2px solid ${isCompleted ? 'rgba(16,185,129,0.3)' : 'rgba(212,165,74,0.35)'}`,
                              }}>
                              {isCompleted ? (
                                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="#10b981" strokeWidth={2.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              ) : isLocked ? (
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="rgba(148,163,184,0.4)" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                              ) : (
                                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
                                  stroke="#d4a54a" style={{ animation: 'crownPulse 2.5s ease-in-out infinite' }}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                                </svg>
                              )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-[9px] tracking-[0.3em] uppercase font-bold px-2 py-0.5 rounded-full"
                                  style={{ background: 'linear-gradient(90deg, rgba(212,165,74,0.15), rgba(180,130,50,0.08))', color: '#d4a54a', border: '1px solid rgba(212,165,74,0.25)' }}>
                                  Border Piece #6
                                </span>
                                <span className="text-[9px] tracking-[0.3em] uppercase font-bold px-2 py-0.5 rounded-full"
                                  style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.2)' }}>
                                  Lesson 13
                                </span>
                              </div>
                              <p className="text-lg font-serif font-bold mb-1" style={{ color: isLocked ? 'rgba(180,140,70,0.25)' : '#d4a54a' }}>
                                {lesson.title}
                              </p>
                              <p className="text-xs leading-relaxed mb-2" style={{ color: isLocked ? 'rgba(180,140,70,0.15)' : 'rgba(212,165,74,0.55)' }}>
                                The crown jewel of the Treasure Map — the keystone that timestamps the rapture event, unlocking and certifying the whole timeline of His coming.
                              </p>
                              {lesson.opening_scripture_ref && (
                                <p className="text-[10px] font-mono tracking-wider" style={{ color: isLocked ? 'rgba(180,140,70,0.12)' : 'rgba(220,38,38,0.6)' }}>
                                  {lesson.opening_scripture_ref}
                                </p>
                              )}
                            </div>

                            {/* Arrow indicator */}
                            {!isLocked && (
                              <svg className="w-5 h-5 flex-shrink-0 self-center opacity-40 group-hover:opacity-100 transition-all transform group-hover:translate-x-1"
                                fill="none" viewBox="0 0 24 24" stroke={isCompleted ? '#10b981' : '#d4a54a'} strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Decorative separator below */}
                      <div className="flex items-center gap-3 mt-4">
                        <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,165,74,0.4), transparent)' }} />
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="rgba(212,165,74,0.4)">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                        <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,165,74,0.4), transparent)' }} />
                      </div>
                    </div>
                  );
                }

                // Standard lesson card
                return (
                  <button
                    key={lesson.lesson_id || moduleKey}
                    onClick={() => !isLocked && onNavigate(`/lesson/${moduleKey}`)}
                    disabled={isLocked}
                    data-testid={`curriculum-${moduleKey}`}
                    className="w-full text-left flex items-center gap-4 px-4 py-3.5 rounded-xl border transition-all duration-300 group"
                    style={{
                      background: isCompleted ? 'rgba(16,185,129,0.04)' : isInProgress ? 'rgba(217,119,6,0.04)' : isLocked ? 'rgba(10,8,4,0.3)' : 'rgba(10,8,4,0.6)',
                      borderColor: isCompleted ? 'rgba(16,185,129,0.2)' : isInProgress ? 'rgba(217,119,6,0.2)' : isLocked ? 'rgba(60,40,10,0.08)' : 'rgba(120,80,20,0.12)',
                      opacity: isLocked ? 0.45 : 1,
                      cursor: isLocked ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        background: isCompleted ? 'rgba(16,185,129,0.12)' : `${cfg.color}12`,
                        border: `1.5px solid ${isCompleted ? 'rgba(16,185,129,0.3)' : `${cfg.color}30`}`,
                      }}>
                      {isCompleted ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#10b981" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : isLocked ? (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="rgba(148,163,184,0.5)" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      ) : (
                        <span className="text-xs font-bold font-mono" style={{ color: cfg.color }}>{lesson.lesson_num}</span>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[9px] tracking-wider uppercase font-mono px-1.5 py-0.5 rounded"
                          style={{ background: `${cfg.color}10`, color: `${cfg.color}90`, border: `1px solid ${cfg.color}20` }}>
                          {cfg.label}
                        </span>
                        {isInProgress && (
                          <span className="text-[9px] tracking-wider uppercase font-mono px-1.5 py-0.5 rounded"
                            style={{ background: 'rgba(217,119,6,0.1)', color: '#d97706', border: '1px solid rgba(217,119,6,0.2)' }}>
                            In Progress
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-serif font-semibold truncate" style={{ color: isLocked ? 'rgba(180,140,70,0.3)' : theme?.textPrimary || '#e5d5b0' }}>
                        {lesson.title}
                      </p>
                      {lesson.opening_scripture_ref && (
                        <p className="text-[10px] truncate" style={{ color: isLocked ? 'rgba(180,140,70,0.15)' : theme?.textMuted || 'rgba(180,140,70,0.45)' }}>
                          {lesson.opening_scripture_ref}
                        </p>
                      )}
                    </div>

                    {!isLocked && (
                      <svg className="w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-1"
                        fill="none" viewBox="0 0 24 24" stroke={isCompleted ? '#10b981' : cfg.color} strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
